/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

import { some } from 'lodash';

import { getUserLanguageCode } from 'js/lib/language';

import type { Theme } from '@coursera/cds-core';
import { InlineNotification } from '@coursera/cds-core';

import { useGetNotificationType } from 'bundles/course-notifications-v2/components/translatedContent/TranslationNotificationHooks';
import ErrorBoundaryWithLogging from 'bundles/page/components/ErrorBoundaryWithLogging';

import _t from 'i18n!nls/course-notifications-v2';

type Props = {
  courseId: string;
  cmlRef?: RefObject<HTMLDivElement>;
  show?: boolean;
};

const LOCAL_URL_REGEX = /^(https?:\/\/)?(www\.)?(learner.)?coursera\.(org|help|support)/;

function checkForExternalContent(rootElement: HTMLDivElement) {
  const elements = rootElement.querySelectorAll('a, iframe');
  const hasSomeExternalContent = some(elements, (element: HTMLElement) => {
    // check the href (anchors) and src (iframes) to see if they point to urls outside coursera
    const url = element.getAttribute('href') ?? element.getAttribute('src');

    // if the element doesn't have an url, then it doesn't have external content
    if (!url) return false;

    // if the url points to a coursera page, it doesn't have external content
    if (url.match(LOCAL_URL_REGEX)) return false;

    // has an url that is not a coursera page
    return true;
  });

  return elements.length > 0 && hasSomeExternalContent;
}

const useExternalCmlContent = (cmlRef?: RefObject<HTMLDivElement>, show?: boolean) => {
  const [hasExternalContent, setHasExternalContent] = useState(false);

  useEffect(() => {
    if (show || !cmlRef) {
      setHasExternalContent(!!show);
      return;
    }
    const interval = setInterval(() => {
      // Re-trying until cml has loaded
      if (cmlRef.current) {
        // once cml is loaded, clear the intarval and set the external content
        clearInterval(interval);
        setHasExternalContent(checkForExternalContent(cmlRef.current));
      }
    }, 50);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [cmlRef, show]);

  return hasExternalContent;
};

const styles = {
  banner: ({ spacing }: Theme) => css`
    margin-bottom: ${spacing(16)};
  `,
};

const ExternalContentBannerInternal = ({ courseId, cmlRef, show }: Props) => {
  const notification = useGetNotificationType(courseId, getUserLanguageCode());
  const hasExternalContent = useExternalCmlContent(cmlRef, show);
  const isNotOriginalLanguage = notification?.type === 'AUTO_TRANSLATED' || notification?.type === 'NO_TRANSLATION';

  if (hasExternalContent && isNotOriginalLanguage) {
    return (
      <InlineNotification css={styles.banner} severity="information">
        {_t('This item includes content that hasn’t been translated into your preferred language yet.')}
      </InlineNotification>
    );
  }

  return null;
};

const ExternalContentBanner = (props: Props) => {
  return (
    <ErrorBoundaryWithLogging>
      <ExternalContentBannerInternal {...props} />
    </ErrorBoundaryWithLogging>
  );
};

export { ExternalContentBannerInternal, checkForExternalContent };
export default ExternalContentBanner;
